import type { CriteriaLabel } from '@piccolohealth/pbs-common';
import {
  ChakraV3,
  MultiSelect,
  MultiSelectComponents,
  type MultiSelectOption,
  type MultiSelectProps,
  type OnChangeRequest,
  SelectCheck,
} from '@piccolohealth/ui';
import { P, uuid } from '@piccolohealth/util';
import React from 'react';
import { useController } from 'react-hook-form';
import { FaCheck, FaEllipsisH, FaPlus } from 'react-icons/fa';

interface CriteriaLabelColorItemsProps {
  label: CriteriaLabel;
  onChange: (label: CriteriaLabel) => void;
}

const CriteriaLabelColorItems = (props: CriteriaLabelColorItemsProps) => {
  const { label } = props;

  const colors = ['blue', 'cyan', 'green', 'orange', 'pink', 'purple', 'red', 'teal', 'yellow'];

  const onClick = (newColor: string) => {
    props.onChange({ ...label, color: newColor });
  };

  return (
    <ChakraV3.List.Root display='inline-block' overflowY='auto' w='full'>
      {colors.map((color, index) => {
        return (
          <ChakraV3.List.Item
            key={`${color}${index}`}
            px={3}
            py={1}
            display='flex'
            flexDir='row'
            alignItems='center'
            onClick={() => onClick(color)}
            cursor='pointer'
          >
            <ChakraV3.Tag colorPalette={color} size='sm'>
              {P.upperFirst(color)}
            </ChakraV3.Tag>
            <ChakraV3.Spacer />
            {color === label.color ? <FaCheck /> : null}
          </ChakraV3.List.Item>
        );
      })}
    </ChakraV3.List.Root>
  );
};

interface ManageCriteriaLabelMenuProps {
  label: CriteriaLabel;
  onChange: (label: CriteriaLabel) => void;
}

export const ManageCriteriaLabelMenu = (props: ManageCriteriaLabelMenuProps) => {
  const { label, onChange } = props;

  return (
    <ChakraV3.Menu.Root closeOnSelect={false} positioning={{ strategy: 'fixed' }} lazyMount>
      <ChakraV3.Menu.Trigger asChild>
        <ChakraV3.IconButton
          variant='ghost'
          size='2xs'
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          <FaEllipsisH />
        </ChakraV3.IconButton>
      </ChakraV3.Menu.Trigger>
      <ChakraV3.Menu.Positioner>
        <ChakraV3.Menu.Content zIndex={1900}>
          <ChakraV3.Menu.ItemGroup
            title='Color'
            color='gray.500'
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
          >
            <ChakraV3.Menu.ItemGroupLabel>Color</ChakraV3.Menu.ItemGroupLabel>
            <CriteriaLabelColorItems label={label} onChange={onChange} />
          </ChakraV3.Menu.ItemGroup>
        </ChakraV3.Menu.Content>
      </ChakraV3.Menu.Positioner>
    </ChakraV3.Menu.Root>
  );
};

const labelToOption = (label: CriteriaLabel): MultiSelectOption<CriteriaLabel> => {
  return {
    label: label.name,
    value: label.id,
    raw: label,
  };
};

interface Props
  extends Omit<MultiSelectProps<CriteriaLabel>, 'options' | 'selectedValues' | 'onChange'> {
  name: string;
  availableLabels: CriteriaLabel[];
  onUpdateCriteriaLabels: (labels: CriteriaLabel[]) => void;
}

export const HookedCriteriaLabelsControl = (props: Props) => {
  const { name, isLoading, isDisabled, variant, availableLabels, onUpdateCriteriaLabels, ...rest } =
    props;

  const { field } = useController({ name });
  const value: string[] = field.value || [];

  const selectedLabels = React.useMemo(() => {
    return value
      .map((labelId) => availableLabels.find((label) => label.id === labelId))
      .filter(Boolean) as CriteriaLabel[];
  }, [value, availableLabels]);

  const options: MultiSelectOption<CriteriaLabel>[] = React.useMemo(() => {
    const uniqueLabels = P.uniqBy(availableLabels, (l) => l.id);
    return uniqueLabels.map(labelToOption);
  }, [availableLabels]);

  const selectedValues = React.useMemo(() => {
    return selectedLabels.map(labelToOption);
  }, [selectedLabels]);

  const onChangePrime = React.useCallback(
    (request: OnChangeRequest<CriteriaLabel>) => {
      switch (request.action) {
        case 'CREATE': {
          const newLabel = {
            id: uuid(),
            name: request.value.value,
            color: 'blue',
          };
          // Add to the labelIds field
          field.onChange([...value, newLabel.id]);
          // Update top-level criteriaLabels if callback provided
          if (onUpdateCriteriaLabels) {
            const updatedLabels = [...availableLabels, newLabel];
            onUpdateCriteriaLabels(updatedLabels);
          }
          break;
        }
        case 'ADD': {
          const existingLabel = availableLabels.find((l) => l.id === request.value.value);
          if (existingLabel) {
            field.onChange([...value, existingLabel.id]);
          }
          break;
        }
        case 'REMOVE': {
          field.onChange(value.filter((labelId: string) => labelId !== request.value.value));
          break;
        }
      }
    },
    [field, value, availableLabels, onUpdateCriteriaLabels],
  );

  const onChangeLabel = React.useCallback(
    (newLabel: CriteriaLabel) => {
      const updatedLabels = availableLabels.map((label) =>
        label.id === newLabel.id ? newLabel : label,
      );
      onUpdateCriteriaLabels(updatedLabels);
    },
    [availableLabels, onUpdateCriteriaLabels],
  );

  return (
    <MultiSelect
      {...rest}
      options={options}
      selectedValues={selectedValues}
      isLoading={isLoading}
      isCreatable={true}
      isDisabled={isDisabled}
      onChange={onChangePrime}
      variant={variant}
      placeholder='Add a label'
      size='sm'
      bg='transparent'
      components={{
        Value: (props) => (
          <ChakraV3.Tag
            size='sm'
            variant='subtle'
            colorPalette={props.option.raw.color}
            onClose={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onChangePrime({
                value: props.option,
                values: selectedValues.filter((v) => v.value !== props.option.value),
                action: 'REMOVE',
              });
            }}
          >
            {props.option.label}
          </ChakraV3.Tag>
        ),

        Option: (props) => {
          const isNewLabel = P.isString(props.option.raw) && props.option.raw === 'CREATABLE';

          const text = isNewLabel ? (
            <>
              Create label
              <ChakraV3.Text fontWeight='bold'>&nbsp;{`"${props.option.label}"`}</ChakraV3.Text>
            </>
          ) : (
            <ChakraV3.Text>{props.option.label}</ChakraV3.Text>
          );

          return (
            <ChakraV3.HStack w='full'>
              <ChakraV3.Tag
                size='sm'
                colorPalette={props.option.raw.color ?? 'blue'}
                maxW='100%'
                labelProps={{
                  display: 'inline-flex',
                  lineHeight: 'normal',
                }}
                startElement={isNewLabel && <FaPlus />}
              >
                {text}
              </ChakraV3.Tag>
              <ChakraV3.Spacer />
              <SelectCheck />
              {!isNewLabel && (
                <ManageCriteriaLabelMenu label={props.option.raw} onChange={onChangeLabel} />
              )}
            </ChakraV3.HStack>
          );
        },
        Footer: () => (
          <MultiSelectComponents.Footer>
            <ChakraV3.Text color='gray.500' fontSize='xs' fontWeight='semibold'>
              Select a label or create one
            </ChakraV3.Text>
          </MultiSelectComponents.Footer>
        ),
      }}
    />
  );
};
